import './main.scss';

import { loadComponents } from '@opten/gin-tonic/helpers/components';
import { SidebarComponent } from './components/sidebar';
import { SlideshowComponent } from './components/slideshow';
import './components/wordcloud';
import { MapComponent } from './components/map';
import { MobileMenuComponent } from './components/mobilemenu';
import { infiniteScroll } from './components/infinite-scroll';
import { tcPinchZoom } from './components/pinch-zoom';
import { initPlayScroll } from './components/play-scroll';
import { initPlayCountdown } from './components/play-countdown';
import { initContentInset } from './components/content-inset';
import { initPlayCtaReveal } from './components/play-cta-reveal';
import { initWebVitals } from './components/web-vitals';

initWebVitals();
loadComponents(SidebarComponent, SlideshowComponent, MobileMenuComponent);
initPlayScroll();
initPlayCountdown();
initContentInset();
initPlayCtaReveal();

function loadElemIfReady(
	selector: string,
	callback: (elem: HTMLElement) => void
) {
	const timer = setInterval(() => {
		const elem = document.querySelector(selector) as HTMLElement | null;
		if (elem != null) {
			clearInterval(timer);
			callback(elem);
		}
	}, 500);
}

document.addEventListener('DOMContentLoaded', () => {
	const slideshowElem = document.querySelector(
		SlideshowComponent.CONTAINER_SELECTOR
	) as HTMLElement | null;
	if (slideshowElem) {
		new SlideshowComponent(slideshowElem);
	}
});

// load google maps

let googleMapsLoaded = false;

// expose functions to window (called from server-side scripts / external libs)
declare global {
	interface Window {
		loadMap?: (dotNetReference: unknown, element?: HTMLElement) => void;
		googleMapsLoaded?: () => void;
		scrollToTop?: () => void;
	}
}

// the callback can fire before this script defined it, so google.maps itself counts as loaded too
function whenGoogleMapsReady(callback: () => void) {
	const isReady = () =>
		googleMapsLoaded || (typeof google !== 'undefined' && google.maps?.Map != null);
	if (isReady()) {
		callback();
		return;
	}
	const timer = setInterval(() => {
		if (isReady()) {
			clearInterval(timer);
			callback();
		}
	}, 300);
}

// LocationMap passes its own element; without one the first .js-map on the page gets the map
window.loadMap = (dotNetReference, element) => {
	whenGoogleMapsReady(() => {
		if (element) {
			new MapComponent(element, dotNetReference);
		} else {
			loadElemIfReady('.js-map', (elem) => new MapComponent(elem, dotNetReference));
		}
	});
};

window.googleMapsLoaded = () => {
	googleMapsLoaded = true;
};

// the content scrolls in .singha-body, on phones in the play views the page itself
// (_play-shell.scss) — scroll whichever it is
window.scrollToTop = function scrollToTop() {
	document.querySelectorAll('.singha-layout, .singha-layout .singha-body').forEach((el) => {
		el.scrollTo({ top: 0, behavior: 'smooth' });
	});
	window.scrollTo({ top: 0, behavior: 'smooth' });
};

window.singhaInfiniteScroll = infiniteScroll;

// Pinch-zoom/pan for the play-shell map view (called from Blazor interop).
window.tcPinchZoom = tcPinchZoom;

window.blazorCulture = {
	get: () => window.localStorage['BlazorCulture'],
	set: (value: string) => (window.localStorage['BlazorCulture'] = value),
};

window.clipboardCopy = {
	copyText: function (text: string) {
		navigator.clipboard
			.writeText(text)
			.then(function () {
				//alert("Copied to clipboard!");
			})
			.catch(function (error) {
				console.error(error);
			});
	},
};

type ContentStreamReference = {
	arrayBuffer: () => Promise<ArrayBuffer>;
};

window.downloadFileFromStream = async (
	fileName: string,
	contentStreamReference: ContentStreamReference
) => {
	const arrayBuffer = await contentStreamReference.arrayBuffer();
	const blob = new Blob([arrayBuffer]);
	const url = URL.createObjectURL(blob);
	const anchorElement = document.createElement('a');
	anchorElement.href = url;
	anchorElement.download = fileName ?? '';
	anchorElement.click();
	anchorElement.remove();
	URL.revokeObjectURL(url);
};

window.fireGA4Event = function fireGA4Event(eventName, eventParams) {
	if (window.dataLayer) {
		window.dataLayer.push({
			event: eventName,
			page_url: window.location.href, // Capture current page URL
			...eventParams,
		});
	}
};

window.convertAndResizeHeic = async (
	file: File,
	maxWidth: number,
	maxHeight: number
) => {
	try {
		const heicBlob = new Blob([file], { type: 'image/heic' });

		// Convert HEIC to JPEG
		let convertedBlob;
		try {
			convertedBlob = await heic2any({
				blob: heicBlob,
				toType: 'image/jpeg',
			});
		} catch (conversionError) {
			throw new Error(
				'Error during HEIC conversion: ' + conversionError.message
			);
		}

		// Resize and create the canvas with the converted image
		return new Promise((resolve, reject) => {
			const img = new Image();
			const url = URL.createObjectURL(convertedBlob);

			img.onload = () => {
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');

				const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
				canvas.width = img.width * scale;
				canvas.height = img.height * scale;

				ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

				canvas.toBlob(
					(blob) => {
						if (!blob) {
							reject(new Error('Failed to generate image blob from canvas.'));
							return;
						}
						resolve(blob); // Successfully resized
					},
					'image/jpeg',
					0.8 // Quality setting
				);
			};

			img.onerror = (error) => {
				reject(new Error('Image loading error: ' + error.message));
			};

			img.src = url; // Trigger image loading
		});
	} catch (error) {
		return Promise.reject(error); // Propagate error
	}
};
